export const DEFAULT_MAP_PIN_COORDINATE = {
    lat: 49.8951,
    lng: -97.1384,
};

export const MAP_PLACE_RESULT_LIMIT = 8;

const readCoordinateValue = (value) => (
    typeof value === 'function' ? value() : value
);

const cleanText = (value) => String(value ?? '').trim();

const NAMED_PLACE_TYPES = new Set(['establishment', 'point_of_interest']);
const ADDRESS_ONLY_PLACE_TYPES = new Set([
    'country',
    'floor',
    'geocode',
    'intersection',
    'locality',
    'neighborhood',
    'political',
    'post_box',
    'premise',
    'room',
    'route',
    'street_address',
    'subpremise',
]);

const normalizeAddressText = (value) => cleanText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const isAddressOnlyPlaceType = (type) => (
    ADDRESS_ONLY_PLACE_TYPES.has(type)
    || /^(administrative_area|postal_code|sublocality)/.test(type)
);

const getApplicableMapPlaceName = (label, address, types = []) => {
    if (!label) return '';
    if (types.some((type) => NAMED_PLACE_TYPES.has(type))) return label;
    if (types.length === 0 || types.every(isAddressOnlyPlaceType)) return '';

    const normalizedLabel = normalizeAddressText(label);
    if (
        !normalizedLabel
        || normalizedLabel === normalizeAddressText(address)
        || normalizedLabel === normalizeAddressText(address.split(',')[0])
        || /^\d+[a-z]?(?:[\s-]|$)/i.test(label)
    ) {
        return '';
    }

    return label;
};

export const normalizeMapPinCoordinate = (coordinate) => {
    const rawLatitude = readCoordinateValue(
        coordinate?.lat ?? coordinate?.latitude,
    );
    const rawLongitude = readCoordinateValue(
        coordinate?.lng ?? coordinate?.longitude,
    );
    const hasBlankCoordinate = [rawLatitude, rawLongitude].some((value) => (
        value == null || (typeof value === 'string' && value.trim() === '')
    ));

    if (hasBlankCoordinate) return null;

    const lat = Number(rawLatitude);
    const lng = Number(rawLongitude);
    const isValid = Number.isFinite(lat)
        && Number.isFinite(lng)
        && lat >= -90
        && lat <= 90
        && lng >= -180
        && lng <= 180;

    return isValid ? {lat, lng} : null;
};

export const getMapEventCoordinate = (event) => (
    normalizeMapPinCoordinate(event?.latLng ?? event?.coordinate ?? event)
);

export const formatPinnedLocationLabel = (coordinate) => {
    const normalizedCoordinate = normalizeMapPinCoordinate(coordinate);
    if (!normalizedCoordinate) return '';

    return `Pinned location — ${normalizedCoordinate.lat.toFixed(6)}, `
        + normalizedCoordinate.lng.toFixed(6);
};

export const parseGooglePlacesMapResults = (
    results,
    limit = MAP_PLACE_RESULT_LIMIT,
) => {
    if (!Array.isArray(results) || limit <= 0) return [];

    const parsedResults = [];
    const seenPlaceIds = new Set();

    for (const result of results) {
        const placeId = cleanText(result?.place_id);
        const address = [result?.formatted_address, result?.vicinity]
            .map(cleanText)
            .find(Boolean) ?? '';
        const label = cleanText(result?.name) || address;
        const position = normalizeMapPinCoordinate(result?.geometry?.location);

        if (!placeId || !label || !position || seenPlaceIds.has(placeId)) {
            continue;
        }

        seenPlaceIds.add(placeId);
        const types = Array.isArray(result?.types) ? result.types : [];
        const placeName = getApplicableMapPlaceName(label, address, types);

        parsedResults.push({
            address,
            label,
            placeId,
            placeName: placeName || null,
            position,
        });

        if (parsedResults.length === Math.min(limit, MAP_PLACE_RESULT_LIMIT)) {
            break;
        }
    }

    return parsedResults;
};
